<script setup lang="ts">
const props = defineProps<{ store: SFCCStore }>()
const emit = defineEmits<{
  (e: 'datalayer-click', linkDetails: { label: string; url?: string }): void
}>()
const { locale } = useI18n()
const storeUrl = computed(() => getStoreUrl(props.store, locale.value === 'fr' ? '/fr/stores' : '/stores'))
</script>

<template>
  <article class="store-my-store-card" data-testid="store-my-store-card">
    <div class="store-my-store-card__heading">
      <h3 class="store-my-store-card__name" data-testid="store-my-store-card__name">
        {{ tidyStoreName(store.name) }}
      </h3>
      <StoreMyStoreBadge />
    </div>
    <p class="store-my-store-card__address" data-testid="store-my-store-card__address">
      {{ formatStoreAddress(store) }}
    </p>
    <div class="store-my-store-card__contact">
      <a
        v-if="store.phone"
        data-testid="store-my-store-card__phone"
        :href="`tel:${store.phone}`"
        @click="emit('datalayer-click', { label: 'phone number', url: `tel:${store.phone}` })"
        >{{ store.phone }}</a
      >
      <Anchor
        variant="text"
        class="store-my-store-card__store-page"
        data-testid="store-my-store-card__store-page"
        :url="storeUrl"
        @click="emit('datalayer-click', { label: $t('set_my_store.view_store_page'), url: storeUrl })"
      >
        {{ $t('set_my_store.view_store_page') }}
      </Anchor>
    </div>
    <StoreHours class="store-my-store-card__hours" :store="store" :position="0" is-slide-in />
    <StoreLinks
      class="store-my-store-card__links"
      :store="store"
      :position="0"
      @datalayer-click="emit('datalayer-click', $event)"
    />
  </article>
</template>

<style lang="postcss">
.store-my-store-card {
  display: flex;
  flex-direction: column;
  padding: var(--space-60);
  background-color: var(--surface-light-00);
  border: 1px solid var(--border-neutral-20);
  border-radius: var(--border-radius-4);
  box-shadow:
    0 1px 4px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px 0 rgba(0, 0, 0, 0.1);
  font-size: var(--text-base);
  line-height: var(--line-height-default);

  .store-my-store-card__heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-20);
  }

  .store-my-store-card__name {
    margin: 0;
    font-size: var(--text-xlarge);
    font-weight: bold;
    letter-spacing: var(--letter-spacing-default);
    line-height: var(--line-height-default);
    text-transform: none;
  }

  .store-my-store-card__address {
    margin: var(--space-30) 0 0;
  }

  .store-my-store-card__contact {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--space-30);
    margin-top: var(--space-50);
    padding-bottom: var(--space-50);
    border-bottom: 1px solid var(--border-neutral-20);
  }

  .store-my-store-card__store-page {
    margin-left: auto;
  }

  /* Always open here, so StoreHours' accordion chrome is stripped back to the table. */
  .store-my-store-card__hours {
    margin-top: var(--space-50);

    .accordion details {
      border-bottom: none;
    }

    .accordion .content {
      padding-bottom: 0;
    }

    .accordion.store-hours__accordion summary {
      padding-top: 0;
      padding-bottom: var(--space-30);
      font-size: var(--text-base);
      font-weight: normal;
    }

    .store-hours__hours-title {
      font-weight: normal;
    }

    .store-hours__item {
      grid-template-columns: 1fr auto;
      align-items: center;
      min-height: 1.75rem; /* 28px */
      margin-bottom: 0;
      padding-block: 0;
    }

    .store-hours__title {
      padding: 0 var(--space-30) 0 var(--space-50);
    }

    .store-hours__description {
      gap: 0;
      color: var(--text-secondary);

      span {
        min-width: 4.5rem;
        padding: 0 var(--space-30);
      }

      span:last-child {
        padding-right: var(--space-50);
      }
    }

    .is-active .store-hours__description {
      color: var(--text-primary);
    }
  }

  .store-my-store-card__links {
    margin: var(--space-50) 0 0;
    font-size: var(--text-small);

    .store-links__book-appointment,
    .store-links__directions {
      gap: var(--space-20);
    }

    .store-links__book-appointment__icon,
    .store-links__directions__icon {
      width: var(--space-60);
      height: var(--space-60);
      margin-right: 0;
    }
  }
}
</style>
